import { NgForm } from '@angular/forms';

/**
 * Identifies and returns a list of review periods that are missing mandatory dates.
 * 
 * This function scans the survey form's controls for "reviewedFrom" and "reviewedTo" fields,
 * grouping them by question number. If either date is missing for a given question, 
 * the corresponding review period is included in the returned list.
 * 
 * **Note:** The form control names contain question numbers that are **zero-indexed**.
 * For example, `question_0reviewedFrom` corresponds to **Question 1** in the UI, 
 * and `question_1reviewedTo` corresponds to **Question 2**, etc.
 * 
 * @param {NgForm} form - The survey form containing review period controls.
 * @returns {Array<{ questionNumber: number, hasFromDate?: boolean, hasToDate?: boolean }>} 
 * An array of objects representing invalid review periods, where either the "reviewedFrom" 
 * or "reviewedTo" date is missing.
 */  
export function getInvalidMandatoryReviewPeriodDates(form: NgForm): object[] {
  if (!form || !form.controls || Object.keys(form.controls).length === 0) {
    return []; // Return an empty array for empty or undefined forms
  }  
  const reviewPeriods = new Map<number, { questionNumber: number; hasFromDate?: boolean; hasToDate?: boolean }>();

  for (const name in form.controls) {
    const value = form.controls[name].value;
    const isReviewedFrom = name.endsWith('reviewedFrom');
    const isReviewedTo = name.endsWith('reviewedTo');

    // eslint-disable-next-line no-continue
    if (!isReviewedFrom && !isReviewedTo) continue;

    // Extract question number
    const question = name.split('_').pop() ?? '';
    const questionNumber = parseInt(question.split('r')[0], 10);
    const visualQuestionNumber = questionNumber + 1;

    // Get or create review period
    if (!reviewPeriods.has(visualQuestionNumber)) {
      reviewPeriods.set(visualQuestionNumber, { questionNumber: visualQuestionNumber });
    }
    const reviewPeriod = reviewPeriods.get(visualQuestionNumber)!;

    // Update properties
    if (isReviewedFrom) reviewPeriod.hasFromDate = !!value;
    if (isReviewedTo) reviewPeriod.hasToDate = !!value;
  }

  // Return only invalid review periods
  return Array.from(reviewPeriods.values()).filter((rp) => !rp.hasFromDate || !rp.hasToDate);
} 
